import { formatCurrency } from '@/lib/format';
import type { CatalogEvent } from '@/types/product';
import { BILLING_PERIOD_LABELS, type BillingPeriod } from '@/types/product';
import type { ProductDiff } from '../../../../backend/src/utils/productDiff';

const money = (cents: number | null | undefined) =>
  cents == null ? '—' : formatCurrency(cents / 100);
const hourValue = (value: number | string | null) =>
  value === null
    ? '—'
    : typeof value === 'number'
      ? `${new Intl.NumberFormat('nl-NL').format(value)} uur`
      : value;
const modes: Record<string, string> = {
  hours: 'uren × tarief',
  fixed: 'vaste prijs',
  on_request: 'op aanvraag',
};

/** Also reads historical events written before detailed workbench diffs existed. */
export function describeProductDiff(event: CatalogEvent): string[] {
  const diff = event.diff as
    | (Partial<ProductDiff> & {
        fromCents?: number | null;
        toCents?: number;
        validFrom?: string;
        productsAffected?: number;
        quotesAffected?: number;
        replacementName?: string;
        billingPeriodChanged?: { from: BillingPeriod; to: BillingPeriod } | null;
      })
    | null;
  if (!diff) return [];
  const parts: string[] = [];
  if (event.type === 'rate_removed') {
    if (diff.replacementName) parts.push(`Vervangend tarief: ${diff.replacementName}`);
    if (diff.productsAffected !== undefined)
      parts.push(`${diff.productsAffected} producten omgezet`);
    return parts;
  }
  if (event.type === 'rate_changed') {
    if (diff.toCents !== undefined) parts.push(`${money(diff.fromCents)} → ${money(diff.toCents)}`);
    if (diff.validFrom)
      parts.push(`Geldig vanaf ${new Date(diff.validFrom).toLocaleDateString('nl-NL')}`);
    if (diff.productsAffected !== undefined)
      parts.push(
        `${diff.productsAffected} producten en ${diff.quotesAffected ?? 0} lopende offertes geraakt`
      );
    return parts;
  }
  if (diff.firstPublication) parts.push('Eerste publicatie');
  if (diff.billingPeriodChanged)
    parts.push(
      `Facturatieperiode: ${BILLING_PERIOD_LABELS[diff.billingPeriodChanged.from]} → ${BILLING_PERIOD_LABELS[diff.billingPeriodChanged.to]}`
    );
  for (const price of diff.prices ?? []) {
    const name = price.variantName || price.variantId;
    parts.push(
      price.fromCents === undefined
        ? `${name}: ${money(price.toCents)}`
        : `${name}: ${money(price.fromCents)} → ${money(price.toCents)}`
    );
  }
  for (const variant of diff.variantsRenamed ?? [])
    parts.push(`Variantnaam: ${variant.from} → ${variant.to}`);
  if (diff.variantsAdded?.length)
    parts.push(`Varianten toegevoegd: ${diff.variantsAdded.join(', ')}`);
  if (diff.variantsRemoved?.length)
    parts.push(`Varianten verwijderd: ${diff.variantsRemoved.join(', ')}`);
  if (diff.linesAdded?.length) parts.push(`Regels toegevoegd: ${diff.linesAdded.join(', ')}`);
  if (diff.linesRemoved?.length) parts.push(`Regels verwijderd: ${diff.linesRemoved.join(', ')}`);
  for (const line of diff.linesChanged ?? []) {
    if (line.descriptionChanged)
      parts.push(`Werkzaamheid: ${line.descriptionChanged.from} → ${line.descriptionChanged.to}`);
    if (line.codeChanged)
      parts.push(
        `${line.description} · formuleverwijzing: ${line.codeChanged.from} → ${line.codeChanged.to}`
      );
    if (line.rateChanged) {
      const rate = line.rateChanged;
      parts.push(
        `${line.description} · tarief: ${rate.fromName} (${money(rate.fromCents)}) → ${rate.toName} (${money(rate.toCents)})`
      );
    }
    for (const hours of line.hoursChanged ?? [])
      parts.push(
        `${line.description} · ${hours.variantName}: ${hourValue(hours.from)} → ${hourValue(hours.to)}`
      );
    if (line.visibilityChanged)
      parts.push(`${line.description}: zichtbaarheid op de offerte gewijzigd`);
  }
  if (diff.lineOrderChanged) parts.push('Volgorde van werkzaamheden gewijzigd');
  if (diff.priceModeChanged)
    parts.push(
      `Prijs berekenen met: ${modes[diff.priceModeChanged.from] ?? diff.priceModeChanged.from} → ${modes[diff.priceModeChanged.to] ?? diff.priceModeChanged.to}`
    );
  return parts;
}
